import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Loader, Trash2 } from 'lucide-react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { uploadFile, validateFile, formatFileSize } from '../../services/storageService';
import './StorageUpload.css';

const StorageUpload = ({ clients, onClose, onSuccess, currentUser }) => {
  const [selectedClient, setSelectedClient] = useState('');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [selectedMilestone, setSelectedMilestone] = useState('');
  const [category, setCategory] = useState('draft');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const uploadingRef = useRef(false);

  useEffect(() => {
    if (selectedClient) {
      loadClientProjects(selectedClient);
    } else {
      setProjects([]);
      setSelectedProject('');
      setMilestones([]);
      setSelectedMilestone('');
    }
  }, [selectedClient]);

  useEffect(() => {
    if (selectedProject) {
      loadProjectMilestones(selectedProject);
    } else {
      setMilestones([]);
      setSelectedMilestone('');
    }
  }, [selectedProject]);

  const loadClientProjects = async (clientId) => {
    try {
      const projectsQuery = query(
        collection(db, 'projects'),
        where('clientId', '==', clientId),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(projectsQuery);
      const projectsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProjects(projectsList);
      if (projectsList.length === 1) setSelectedProject(projectsList[0].id);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadProjectMilestones = async (projectId) => {
    try {
      const q = query(
        collection(db, 'milestones'),
        where('projectId', '==', projectId),
        orderBy('order', 'asc')
      );
      const snapshot = await getDocs(q);
      setMilestones(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setSelectedMilestone('');
    } catch (error) {
      console.error('Error loading milestones:', error);
      setMilestones([]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileInput = (e) => {
    if (e.target.files?.length) addFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const addFiles = (newFiles) => {
    const valid = [];
    for (const f of newFiles) {
      const validation = validateFile(f);
      if (!validation.valid) { alert(`${f.name}: ${validation.error}`); continue; }
      valid.push(f);
    }
    setFiles(prev => [...prev, ...valid]);
  };

  const removeFile = (index) => setFiles(prev => prev.filter((_, i) => i !== index));

  const handleUpload = async () => {
    if (uploadingRef.current) return;
    if (!selectedClient || !selectedProject || !category || files.length === 0) {
      alert('Please fill all fields and select at least one file');
      return;
    }
    uploadingRef.current = true;

    const client = clients.find(c => c.id === selectedClient);
    const project = projects.find(p => p.id === selectedProject);
    const milestone = milestones.find(m => m.id === selectedMilestone);

    if (!client || !project) { alert('Invalid client or project'); return; }

    setUploading(true);
    setUploadStatus(files.map(f => ({ name: f.name, progress: 0, done: false, error: null })));

    let allSuccess = true;

    for (let i = 0; i < files.length; i++) {
      const metadata = {
        clientId: selectedClient,
        clientName: client.displayName || client.email,
        projectId: selectedProject,
        projectName: project.name,
        projectStatus: project.status,
        category,
        uploadedBy: currentUser.uid,
        uploadedByName: currentUser.displayName || currentUser.email,
        milestoneId: milestone?.id || null,
        milestoneName: milestone?.title || null,
      };

      try {
        const result = await uploadFile(files[i], metadata, (progress) => {
          setUploadStatus(prev => prev.map((s, idx) => idx === i ? { ...s, progress } : s));
        });

        if (result.success) {
          setUploadStatus(prev => prev.map((s, idx) => idx === i ? { ...s, progress: 100, done: true } : s));
        } else {
          setUploadStatus(prev => prev.map((s, idx) => idx === i ? { ...s, error: result.error } : s));
          allSuccess = false;
        }
      } catch (error) {
        setUploadStatus(prev => prev.map((s, idx) => idx === i ? { ...s, error: error.message } : s));
        allSuccess = false;
      }
    }

    uploadingRef.current = false;
    if (allSuccess) onSuccess();
    else setUploading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content storage-upload-modal">
        <div className="modal-header">
          <h2>Upload Files</h2>
          <button onClick={onClose} className="btn-close" disabled={uploading}><X size={20} /></button>
        </div>

        <div className="upload-form">
          <div className="form-group">
            <label htmlFor="client">Client <span className="required">*</span></label>
            <select id="client" value={selectedClient} onChange={(e) => setSelectedClient(e.target.value)} disabled={uploading} required>
              <option value="">Select client...</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.displayName || client.email}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="project">Project <span className="required">*</span></label>
            <select id="project" value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} disabled={!selectedClient || uploading} required>
              <option value="">{!selectedClient ? 'Select client first...' : 'Select project...'}</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
            {selectedClient && projects.length === 0 && (
              <span className="form-hint">This client has no active projects</span>
            )}
          </div>

          {selectedProject && (
            <div className="form-group">
              <label htmlFor="milestone">Milestone <span className="form-optional">(optional)</span></label>
              <select id="milestone" value={selectedMilestone} onChange={(e) => setSelectedMilestone(e.target.value)} disabled={uploading}>
                <option value="">No milestone</option>
                {milestones.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.title}{m.urgent ? ' 🔥' : ''}{m.status === 'completed' ? ' ✅' : ''}
                  </option>
                ))}
              </select>
              {milestones.length === 0 && (
                <span className="form-hint">This project has no milestones yet</span>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="category">Category <span className="required">*</span></label>
            <select id="category" value={category} onChange={(e) => setCategory(e.target.value)} disabled={uploading} required>
              <option value="draft">Draft (Work in Progress)</option>
              <option value="final">Final (Delivered Work)</option>
            </select>
          </div>

          <div className="form-group">
            <label>Files <span className="required">*</span></label>
            <div
              className={`upload-area ${dragActive ? 'drag-active' : ''} ${files.length > 0 ? 'has-file' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Upload size={40} />
              <p>Drag & drop files here</p>
              <p className="upload-hint">or</p>
              <label className="btn-browse">
                Browse Files
                <input type="file" onChange={handleFileInput} disabled={uploading} multiple hidden />
              </label>
              <p className="upload-limits">Max 50MB per file</p>
            </div>
          </div>

          {files.length > 0 && (
            <div className="upload-file-list">
              {files.map((f, i) => {
                const status = uploadStatus[i];
                return (
                  <div key={i} className="upload-file-item">
                    <div className="upload-file-info">
                      <span className="upload-file-name">{f.name}</span>
                      <span className="upload-file-size">{formatFileSize(f.size)}</span>
                    </div>
                    {status ? (
                      <div className="upload-file-progress">
                        {status.error ? (
                          <span className="upload-file-error">✕ {status.error}</span>
                        ) : status.done ? (
                          <span className="upload-file-done">✓ Done</span>
                        ) : (
                          <>
                            <div className="progress-bar">
                              <div className="progress-fill" style={{ width: `${status.progress}%` }} />
                            </div>
                            <span className="progress-text">{Math.round(status.progress)}%</span>
                          </>
                        )}
                      </div>
                    ) : (
                      !uploading && (
                        <button onClick={() => removeFile(i)} className="btn-remove-file-item" title="Remove">
                          <Trash2 size={14} />
                        </button>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="form-actions">
            <button onClick={onClose} className="btn-cancel" disabled={uploading}>Cancel</button>
            <button
              onClick={handleUpload}
              className="btn-upload-submit"
              disabled={!selectedClient || !selectedProject || files.length === 0 || uploading}
            >
              {uploading ? (
                <><Loader size={16} className="spinner" />Uploading...</>
              ) : (
                <><Upload size={16} />Upload {files.length > 1 ? `${files.length} Files` : 'File'}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorageUpload;
